import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createItemFromTemplate, getTemplatesLibrary } from '../services/item';
import { saveAll, saveMany } from '../store/items';
import { setOpenModales } from '../store/global';
import { NavigationPassword } from '../components/navigation/navigation.Passwords';
import {
  HistorySidebar,
  TemplateCreatePanel,
  TemplateListSection,
} from '../components/templates/template.PageParts';
import {
  getTemplateCreatedItemLabel,
  shouldCreateFromTemplateSelection,
} from '../utils/templateSearch';
import { getStoredAuthUser } from '../utils/auth';
import { getStorageJSON } from '../utils/userStorage';
import {
  getTemplatePathLabel,
  sortByTemplatePathAndLabel,
} from '../utils/templateLibrary';
import { logoSVG } from '../content/logo.svg';

const isValidObjectId = id =>
  typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id);

const PageTemplates = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const items = useSelector(state => state.items);
  const theme = useSelector(state => state.global.theme);
  const isMobile = useSelector(state => state.global.isMobile);
  const navigation = useSelector(state => state.global.navigation);
  const history = useSelector(state => state.global.history || []);
  const [authUser, setAuthUser] = useState(getStoredAuthUser());
  const [templates, setTemplates] = useState([]);
  const [targets, setTargets] = useState([]);
  const [recentItemIds, setRecentItemIds] = useState(() => {
    const saved = getStorageJSON('recentItemIds', []);
    return Array.isArray(saved) ? saved : [];
  });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedUniverseId, setSelectedUniverseId] = useState('');
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [newItemLabel, setNewItemLabel] = useState('');
  const [search, setSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authUser) {
      setLoading(false);
      dispatch(setOpenModales({ id: 'passwords', open: true }));
      return;
    }

    let cancelled = false;
    const loadTemplates = async () => {
      setLoading(true);
      setError('');
      try {
        const library = await getTemplatesLibrary();
        if (cancelled) return;
        const nextTemplates = [...library.templates].sort(
          sortByTemplatePathAndLabel,
        );
        const nextTargets = [...library.targets].sort(
          sortByTemplatePathAndLabel,
        );
        setTemplates(nextTemplates);
        setTargets(nextTargets);
        dispatch(
          saveMany({
            items: [...nextTemplates, ...nextTargets],
            log: 'templates-library',
          }),
        );
        if (nextTargets.length > 0) {
          const firstTarget = nextTargets[0];
          setSelectedTargetId(firstTarget._id);
          setSelectedUniverseId(
            firstTarget.type === 'universe'
              ? firstTarget._id
              : firstTarget.universe || '',
          );
        }
      } catch (e) {
        if (!cancelled) {
          setError('Impossible de charger les templates accessibles.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [authUser, dispatch]);

  useEffect(() => {
    if (authUser) {
      return undefined;
    }
    const interval = window.setInterval(() => {
      const currentUser = getStoredAuthUser();
      if (currentUser) {
        setAuthUser(currentUser);
      }
    }, 500);
    return () => {
      window.clearInterval(interval);
    };
  }, [authUser]);

  useEffect(() => {
    if (!Array.isArray(history) || history.length === 0) return;
    setRecentItemIds(prev =>
      Array.from(new Set([...history, ...prev])).slice(0, 100),
    );
  }, [history]);

  useEffect(() => {
    const missing = recentItemIds.filter(
      recentId => isValidObjectId(recentId) && !items[recentId],
    );
    if (missing.length === 0) {
      return;
    }
    const fetchMissing = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/items/batch`,
          { ids: missing },
        );
        if (Array.isArray(res.data)) {
          res.data.forEach(item => {
            if (item?._id) {
              dispatch(saveAll({ item }));
            }
          });
        }
      } catch (e) {
        // L'historique reste affichable avec les items déjà présents en store.
      }
    };
    fetchMissing();
  }, [recentItemIds, items, dispatch]);

  const filteredTemplates = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return templates;
    }
    return templates.filter(template => {
      const haystack =
        `${template.label || ''} ${getTemplatePathLabel(template)}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [templates, search]);

  const selectedTarget = useMemo(
    () => targets.find(target => target._id === selectedTargetId) || null,
    [targets, selectedTargetId],
  );
  const universeOptions = useMemo(() => {
    const byId = new Map();
    targets.forEach(target => {
      if (target?.type === 'universe') {
        byId.set(target._id, {
          id: target._id,
          label: target.label,
          item: target,
          canCreateInUniverse: true,
        });
        return;
      }
      if (target?.type === 'campaign' && target.universe) {
        const existing = byId.get(target.universe);
        byId.set(target.universe, {
          id: target.universe,
          label: existing?.label || target.path?.[0] || 'Univers',
          item: existing?.item || null,
          canCreateInUniverse: existing?.canCreateInUniverse === true,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      (a.label || '').localeCompare(b.label || ''),
    );
  }, [targets]);
  const selectedUniverseTarget = useMemo(
    () =>
      targets.find(
        target =>
          target.type === 'universe' && target._id === selectedUniverseId,
      ) || null,
    [targets, selectedUniverseId],
  );
  const selectedUniverseCampaignTargets = useMemo(
    () =>
      targets.filter(
        target =>
          target.type === 'campaign' && target.universe === selectedUniverseId,
      ),
    [targets, selectedUniverseId],
  );
  const filteredTargets = useMemo(() => {
    const normalizedSearch = targetSearch.trim().toLowerCase();
    const baseTargets = selectedUniverseCampaignTargets;
    if (!normalizedSearch) {
      return baseTargets;
    }
    return baseTargets.filter(target => {
      const haystack =
        `${target.label || ''} ${getTemplatePathLabel(target)}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [selectedUniverseCampaignTargets, targetSearch]);
  const recentItems = useMemo(
    () => recentItemIds.map(id => items[id]).filter(Boolean),
    [recentItemIds, items],
  );
  const selectedTargetLabel = useMemo(() => {
    if (!selectedTarget) {
      return '';
    }
    if (selectedTarget.type === 'universe') {
      return selectedTarget.label || 'univers sélectionné';
    }
    return getTemplatePathLabel(selectedTarget);
  }, [selectedTarget]);
  const createButtonLabel = useMemo(() => {
    if (creating) {
      return 'Création...';
    }
    const itemLabel = newItemLabel.trim() || 'l’item';
    return `Créer ${itemLabel}${
      selectedTargetLabel ? ` dans ${selectedTargetLabel}` : ''
    }`;
  }, [creating, newItemLabel, selectedTargetLabel]);
  const openItemPage = itemId => {
    if (itemId) {
      navigate(`/item/${itemId}`);
    }
  };
  const handleSelectUniverse = universe => {
    setSelectedUniverseId(universe.id);
    setTargetSearch('');
    const directTarget = targets.find(
      target => target.type === 'universe' && target._id === universe.id,
    );
    const firstCampaign = targets.find(
      target => target.type === 'campaign' && target.universe === universe.id,
    );
    setSelectedTargetId(directTarget?._id || firstCampaign?._id || '');
  };

  const handleSelectTemplate = template => {
    setSelectedTemplate(template);
    setNewItemLabel(getTemplateCreatedItemLabel(template));
  };

  const handleClearTemplate = () => {
    setSelectedTemplate(null);
  };

  const handleCreate = async () => {
    if (
      creating ||
      !shouldCreateFromTemplateSelection(selectedTemplate) ||
      !selectedTarget ||
      !newItemLabel.trim()
    ) {
      return;
    }
    setCreating(true);
    setError('');
    try {
      const result = await createItemFromTemplate(
        selectedTemplate,
        selectedTarget,
        {
          label: newItemLabel.trim(),
        },
      );
      if (result?.item?._id) {
        navigate(`/item/${result.item._id}`);
      }
    } catch (e) {
      setError("Impossible de créer l'item depuis ce template.");
    } finally {
      setCreating(false);
    }
  };

  if (!authUser) {
    return (
      <div className='relative h-screen overflow-y-auto bg-primary-d p-2 text-contrast'>
        <div className='pointer-events-none fixed inset-0 z-0 flex items-center justify-center opacity-10'>
          {logoSVG(theme, { maxHeight: '100%', padding: 100 })}
        </div>
        <NavigationPassword forceOpen={true} />
        <div className='relative z-10 mt-8 w-full rounded-lg bg-main bg-opacity-60 p-2 text-center shadow'>
          <h1 className='font-condensed text-3xl font-bold uppercase'>
            Templates
          </h1>
          <p className='mt-1 text-contrast text-opacity-70'>
            Connecte-toi pour accéder à la bibliothèque de templates.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='relative h-screen overflow-hidden bg-primary-d text-contrast'>
      <div className='pointer-events-none fixed inset-0 z-0 flex items-center justify-center opacity-10'>
        {logoSVG(theme, { maxHeight: '100%', padding: 100 })}
      </div>
      <div className='relative z-10 flex h-full w-full'>
        <div className='min-w-0 flex-1 overflow-y-auto p-2'>
          <div className='flex w-full flex-col gap-2'>
            <header>
              <div className='flex flex-col gap-1 md:flex-row md:items-end md:justify-between'>
                <div>
                  <div className='text-xs font-bold uppercase tracking-wide text-secondary'>
                    Bibliothèque
                  </div>
                  <h1 className='font-condensed text-4xl font-bold uppercase'>
                    Templates
                  </h1>
                  <p className='w-full text-sm text-contrast text-opacity-70'>
                    Crée un nouvel item depuis un template accessible, dans un
                    univers ou dossier où tu as explicitement les droits owner,
                    admin ou éditeur.
                  </p>
                </div>
                <div className='flex gap-1 text-xs text-contrast text-opacity-70'>
                  <div className='rounded bg-main bg-opacity-50 px-1 py-05'>
                    <span className='font-bold text-secondary'>
                      {templates.length}
                    </span>{' '}
                    templates
                  </div>
                  <div className='rounded bg-main bg-opacity-50 px-1 py-05'>
                    <span className='font-bold text-secondary'>
                      {universeOptions.length}
                    </span>{' '}
                    univers
                  </div>
                </div>
              </div>
            </header>

            {error && (
              <div className='rounded-lg bg-red-500 bg-opacity-10 p-1 text-sm shadow'>
                {error}
              </div>
            )}

            <div className='flex w-full flex-col gap-2 lg:flex-row'>
              <TemplateListSection
                loading={loading}
                search={search}
                onSearchChange={setSearch}
                templates={filteredTemplates}
                selectedTemplateId={selectedTemplate?._id}
                onSelectTemplate={handleSelectTemplate}
                onOpenItem={openItemPage}
              />
              <TemplateCreatePanel
                selectedTemplate={selectedTemplate}
                newItemLabel={newItemLabel}
                targetSearch={targetSearch}
                universeOptions={universeOptions}
                selectedUniverseId={selectedUniverseId}
                selectedUniverseTarget={selectedUniverseTarget}
                selectedUniverseCampaignTargets={selectedUniverseCampaignTargets}
                filteredTargets={filteredTargets}
                selectedTargetId={selectedTargetId}
                selectedTarget={selectedTarget}
                targets={targets}
                creating={creating}
                createButtonLabel={createButtonLabel}
                onClearTemplate={handleClearTemplate}
                onNameChange={setNewItemLabel}
                onTargetSearchChange={setTargetSearch}
                onSelectUniverse={handleSelectUniverse}
                onSelectTarget={setSelectedTargetId}
                onOpenItem={openItemPage}
                onCreate={handleCreate}
              />
            </div>
          </div>
        </div>
        {!isMobile && (
          <HistorySidebar
            navigationSize={navigation?.size}
            recentItems={recentItems}
            onOpenPasswords={() =>
              dispatch(setOpenModales({ id: 'managePassword', open: true }))
            }
          />
        )}
      </div>
    </div>
  );
};

export default PageTemplates;
